import { Button, Input, MagellanPopup } from 'components/atoms';
import LanguageSelectCard from 'components/molecules/card/language-select/LanguageSelectCard';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiStarFill } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetLanguages } from 'services/enums';
import { ILanguage } from 'services/enums/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface ILanguageSelectPopupFormProps {
  trigger?: JSX.Element | ((isOpen: boolean) => JSX.Element) | undefined;
  onSelectLanguage?: (language: ILanguage | null, actions: PopupActions) => void;
  initialLanguage?: ILanguage | null;
  displayMode?: boolean;
}

const LanguageSelectPopupForm = ({
  trigger,
  onSelectLanguage,
  initialLanguage = null,
  displayMode,
}: ILanguageSelectPopupFormProps) => {
  const popupRef = useRef<PopupActions | null>(null);
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const [languageSearch, setLanguageSearch] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<ILanguage | null>(null);

  const languages = useGetLanguages(open);

  return displayMode ? (
    <>{trigger}</>
  ) : (
    <Popup
      ref={popupRef}
      open={open}
      onOpen={() => {
        setOpen(true);
        setSelectedLanguage(initialLanguage);
      }}
      onClose={() => {
        setSelectedLanguage(null);
        setLanguageSearch('');
      }}
      modal
      nested
      trigger={trigger}
    >
      <MagellanPopup>
        <MagellanPopup.Header>{t('input.label.language')}</MagellanPopup.Header>

        <Input.Text
          value={languageSearch}
          onChange={(e) => setLanguageSearch(e.target.value)}
          name=""
          appBackground
          placeholder={t('input.placeholder.search-language')}
        />

        <div className="flex flex-col w-full overflow-auto h-[450px] [scrollbar-width:none] gap-8 max-w-[350px]">
          {!languageSearch.length && (
            <div className="flex flex-col w-full gap-2">
              <div className="flex w-full items-center gap-2 px-8 py-4 text-base opacity-65">
                <PiStarFill opacity={0.5} /> {t('popup.suggested-languages')}
              </div>
              {languages.data
                ?.filter((lan) => lan.code.includes(i18n.language))
                .map((language) => (
                  <LanguageSelectCard
                    key={language.code}
                    language={language}
                    isSelected={language.id === selectedLanguage?.id}
                    onClick={(newLanguage) =>
                      setSelectedLanguage((prevLanguage) =>
                        prevLanguage?.id === newLanguage.id ? null : newLanguage,
                      )
                    }
                  />
                ))}
            </div>
          )}
          <div className="flex flex-col w-full gap-2">
            <div className="flex px-8 py-4 text-base opacity-65">{t('popup.all-languages')}</div>
            {languages.data
              ?.filter((lan) => lan.name.toLowerCase().includes(languageSearch.toLowerCase()))
              .map((language) => (
                <LanguageSelectCard
                  language={language}
                  isSelected={language.id === selectedLanguage?.id}
                  onClick={(newLanguage) =>
                    setSelectedLanguage((prevLanguage) =>
                      prevLanguage?.id === newLanguage.id ? null : newLanguage,
                    )
                  }
                />
              ))}
          </div>
        </div>
        <div className="flex justify-between w-full">
          <Button isClear onClick={() => popupRef.current?.close()}>
            <Button.Text text={t('button.cancel')} />
          </Button>
          <Button
            variant={BUTTON_VARIANT.submit}
            onClick={() =>
              popupRef.current &&
              onSelectLanguage &&
              onSelectLanguage(selectedLanguage, popupRef.current)
            }
          >
            <Button.Text text={t('button.save')} />
          </Button>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default LanguageSelectPopupForm;
